import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ENVIRONMENT_IDS, RUN_STAGE_LABELS, type EnvironmentId } from '@sfpbxtestbot/contracts';
import { api } from '../lib/api.js';
import { useAsync } from '../lib/useAsync.js';
import {
  Card,
  EmptyState,
  PageHeader,
  StatusBadge,
  formatDuration,
  formatTimestamp,
} from '../components/primitives.js';

const PAGE_SIZE = 25;

/** Run history, with enough filtering to find the run someone is asking about. */
export function RunsPage(): ReactNode {
  const [search, setSearch] = useState('');
  const [environment, setEnvironment] = useState<EnvironmentId | ''>('');
  const [page, setPage] = useState(0);

  const runs = useAsync(
    () =>
      api.runs({
        limit: PAGE_SIZE,
        offset: page * PAGE_SIZE,
        ...(environment ? { environment } : {}),
        ...(search ? { search } : {}),
      }),
    [page, environment, search],
  );

  const total = runs.data?.total ?? 0;
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="stack">
      <PageHeader
        title="Runs and reports"
        lede="Every run is kept with its assertions and logs, so a result can be reviewed long after the run finished."
      />

      <Card>
        <div className="grid grid--3">
          <label className="field">
            <span className="field__label">Search</span>
            <input
              className="input"
              value={search}
              placeholder="Tenant code or org URL"
              onChange={(event) => {
                setSearch(event.target.value);
                setPage(0);
              }}
            />
          </label>
          <label className="field">
            <span className="field__label">Environment</span>
            <select
              className="select"
              value={environment}
              onChange={(event) => {
                setEnvironment(event.target.value as EnvironmentId | '');
                setPage(0);
              }}
            >
              <option value="">All</option>
              {ENVIRONMENT_IDS.map((id) => (
                <option key={id} value={id}>
                  {id}
                </option>
              ))}
            </select>
          </label>
        </div>
      </Card>

      <Card flush>
        {runs.loading ? (
          <EmptyState title="Loading" body="Fetching run history." />
        ) : total === 0 ? (
          <EmptyState
            title="No runs match"
            body="Try clearing the filters, or start a run from one of the other tabs."
          />
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>Started</th>
                <th>Environment</th>
                <th>Tenant</th>
                <th>Covers</th>
                <th>Result</th>
                <th>Steps</th>
                <th>Duration</th>
              </tr>
            </thead>
            <tbody>
              {runs.data?.runs.map((run) => (
                <tr key={run.id}>
                  <td>
                    <Link to={`/runs/${run.id}`}>{formatTimestamp(run.startedAt ?? run.createdAt)}</Link>
                    {run.dryRun ? <span className="muted"> · dry run</span> : null}
                  </td>
                  <td>{run.environment}</td>
                  <td>{run.tenantCode}</td>
                  <td className="muted" style={{ fontSize: 13 }}>
                    {run.stages.map((stage) => RUN_STAGE_LABELS[stage]).join(', ')}
                  </td>
                  <td>
                    <StatusBadge outcome={run.outcome} />
                  </td>
                  <td>
                    {run.totals ? (
                      <span className="mono">
                        {run.totals.passed}/{run.totals.steps}
                      </span>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td>{formatDuration(run.durationMs)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {pages > 1 ? (
        <div className="row row--between">
          <span className="muted" style={{ fontSize: 13 }}>
            Page {page + 1} of {pages} · {total} runs
          </span>
          <div className="row">
            <button
              type="button"
              className="button button--secondary button--small"
              disabled={page === 0}
              onClick={() => setPage((value) => value - 1)}
            >
              Previous
            </button>
            <button
              type="button"
              className="button button--secondary button--small"
              disabled={page + 1 >= pages}
              onClick={() => setPage((value) => value + 1)}
            >
              Next
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
